import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PackageProgress, pkgActive } from './PackageStatus';
import { toast } from 'sonner';
import {
  X, ChevronLeft, ChevronRight, ChevronDown, Phone, Mail, Globe, MapPin, Star, Flame, Loader2,
  ScanSearch, FileText, Copy, ExternalLink, Instagram, Facebook, Linkedin,
  MessageCircle, Check, ClipboardList, Pencil, Building2, Sparkles, CircleDot, AlertTriangle, RotateCcw,
} from 'lucide-react';
import { ScriptTemplatesModal } from './ScriptTemplatesModal';

const STATUSES = ['new', 'contacted', 'proposal_sent', 'meeting_booked', 'won', 'lost'];
const CHANNELS = [
  { id: 'call', label: 'Call', icon: Phone },
  { id: 'dm', label: 'IG · FB DM', icon: MessageCircle },
  { id: 'linkedin', label: 'LinkedIn', icon: Linkedin },
  { id: 'email', label: 'Email', icon: Mail },
];
const CH_LABEL = { call: 'Call', dm: 'IG · FB DM', linkedin: 'LinkedIn', email: 'Email' };

const BUILD_STEPS = [
  ['sub_account', 'Sub-account'],
  ['brand_profile', 'Brand profile from their website'],
  ['wizard', 'AI agent · knowledge base · chat + voice widgets'],
  ['proposal', 'Proposal with live widget demos'],
];

const copyText = async (text, msg) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(msg || 'Copied');
  } catch { toast.error('Copy failed'); }
};

export const LeadDrawer = ({ theme, api, workspaceId, leadId, leads, settings,
  onNavigate, onClose, onOpenProposal }) => {
  const { isDarkMode: dark } = theme;
  const [data, setData] = useState(null);
  const [channel, setChannel] = useState('call');
  const [pendingTouch, setPendingTouch] = useState(null);
  const [draft, setDraft] = useState(null);
  const [tplOpen, setTplOpen] = useState(false);
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [subs, setSubs] = useState([]);
  const [tick, setTick] = useState(0);
  const bannerRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  const lead = data?.lead;
  const proposal = data?.proposal;
  const scripts = data?.scripts;
  const buildJob = data?.build_job;
  const a = lead?.analysis || {};
  const analyzed = a.status === 'done';
  const running = a.status === 'running';
  const propGenerating = proposal?.status === 'generating';
  const subCreating = lead?.sub_account_status === 'creating';
  const buildRunning = buildJob?.status === 'running';
  const pkgBusy = pkgActive(lead);
  const pkgFailed = lead?.package_status === 'failed';

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/api/workspaces/${workspaceId}/leads/${leadId}`);
      setData(r.data);
      const l = r.data.lead;
      if (l && !l.socials_checked && !Object.keys(l.socials || {}).length && l.website) {
        api.post(`/api/workspaces/${workspaceId}/leads/${leadId}/sniff-socials`)
          .then(sr => setData(d => d && d.lead.lead_id === leadId
            ? { ...d, lead: { ...d.lead, socials: sr.data.socials, socials_checked: true } } : d))
          .catch(() => {});
      }
    } catch { toast.error('Failed to load lead'); closeRef.current(); }
  }, [api, workspaceId, leadId]);

  useEffect(() => { setData(null); setPendingTouch(null); setDraft(null); setBreakdownOpen(false); load(); }, [load]);
  useEffect(() => {
    window.addEventListener('voxinity:lead-updated', load);
    return () => window.removeEventListener('voxinity:lead-updated', load);
  }, [load]);

  useEffect(() => {
    if (!running && !propGenerating && !subCreating && !buildRunning && !pkgBusy) return;
    const t = setInterval(load, running ? 3000 : 4000);
    return () => clearInterval(t);
  }, [running, propGenerating, subCreating, buildRunning, pkgBusy, load]);

  useEffect(() => {
    api.get(`/api/workspaces/${workspaceId}/sub-accounts`)
      .then(r => setSubs(r.data.sub_accounts || r.data || []))
      .catch(() => {});
  }, [api, workspaceId]);

  useEffect(() => {
    if (!running) { setTick(0); return; }
    const t = setInterval(() => setTick(x => x + 1), 1500);
    return () => clearInterval(t);
  }, [running]);
  useEffect(() => { setTick(0); }, [a.progress]);
  const pct = Math.min(95, Math.round(a.progress || 5) + tick);

  useEffect(() => {
    if (!breakdownOpen) return;
    const fn = (e) => { if (bannerRef.current && !bannerRef.current.contains(e.target)) setBreakdownOpen(false); };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [breakdownOpen]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !document.querySelector('[role="dialog"]')) closeRef.current(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const idx = (leads || []).findIndex(l => l.lead_id === leadId);
  const prev = idx > 0 ? leads[idx - 1] : null;
  const next = idx >= 0 && idx < leads.length - 1 ? leads[idx + 1] : null;

  const patch = async (updates) => {
    try {
      await api.put(`/api/workspaces/${workspaceId}/leads/${leadId}`, updates);
      setData(d => ({ ...d, lead: { ...d.lead, ...updates } }));
    } catch { toast.error('Update failed'); }
  };

  const analyze = async () => {
    window.dispatchEvent(new CustomEvent('voxinity:lead-package-request', { detail: { lead_ids: [leadId] } }));
  };

  const logTouch = async (ch) => {
    try {
      const r = await api.post(`/api/workspaces/${workspaceId}/leads/${leadId}/touches`, { channel: ch });
      setData(d => ({ ...d, lead: { ...d.lead, touches: r.data.touches } }));
      toast.success(`Logged — contacted via ${CH_LABEL[ch]}`);
    } catch { toast.error('Failed to log'); }
    setPendingTouch(null);
  };

  const proposalLink = proposal && ['draft', 'sent'].includes(proposal.status)
    ? `${window.location.origin}/p/${proposal.token}` : null;
  const scriptText = scripts
    ? (scripts[channel] || '').split('{proposal_link}').join(proposalLink || '[proposal link]')
    : '';

  const copyDetails = () => {
    if (!lead) return;
    const lines = [
      lead.name,
      [lead.industry, lead.city].filter(Boolean).join(' · '),
      lead.phone && `Phone: ${lead.phone}`,
      `Website: ${lead.website || 'No website'}`,
      lead.address && `Address: ${lead.address}`,
      lead.rating != null && `Google rating: ${lead.rating} (${lead.review_count} reviews)`,
      lead.google_maps_uri && `Maps: ${lead.google_maps_uri}`,
      lead.email && `Email: ${lead.email}`,
      lead.socials?.instagram && `Instagram: ${lead.socials.instagram}`,
      lead.socials?.facebook && `Facebook: ${lead.socials.facebook}`,
      (lead.flags || []).length && `Red flags: ${lead.flags.join(', ')}`,
      analyzed && `Est. revenue leak: $${Number(a.total_monthly_leak_usd || 0).toLocaleString()}/mo · Leak score ${a.leak_score}/100`,
    ].filter(Boolean);
    copyText(lines.join('\n'), 'Business details copied');
  };

  // theme tokens
  const panelBg = dark ? 'bg-[#101216]' : 'bg-white';
  const line = dark ? 'border-white/10' : 'border-slate-200';
  const tp = dark ? 'text-white' : 'text-slate-900';
  const ts = dark ? 'text-white/40' : 'text-slate-500';
  const chipCls = dark
    ? 'bg-white/[0.07] border-white/10 text-teal-300'
    : 'bg-slate-100 border-slate-300 text-slate-700';
  const iconBtn = `p-2 rounded-full border ${line} ${ts} ${dark ? 'hover:bg-white/5 hover:text-white' : 'hover:bg-slate-50 hover:text-slate-800'} transition-colors disabled:opacity-30`;
  const rowIcon = dark ? 'text-white/35' : 'text-slate-400';
  const section = `rounded-2xl border ${line} ${dark ? 'bg-white/[0.02]' : 'bg-slate-50/60'} p-4`;

  return (
    <div className="fixed inset-0 z-[70]" data-testid="lead-drawer">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" onClick={onClose} data-testid="lead-drawer-backdrop" />
      <div className={`absolute right-0 top-0 h-full w-[min(640px,94vw)] xl:w-[42vw] ${panelBg} border-l ${dark ? 'border-emerald-400/50' : 'border-emerald-500/60'} shadow-2xl overflow-y-auto flex flex-col animate-in slide-in-from-right duration-300`}>
        {/* Top bar */}
        <div className={`sticky top-0 z-10 flex items-center justify-between px-5 py-3 border-b ${line} ${panelBg}`}>
          <div className="flex items-center gap-1.5">
            <button onClick={() => prev && onNavigate(prev.lead_id)} disabled={!prev} className={iconBtn} data-testid="lead-drawer-prev" title="Previous lead">
              <ChevronLeft size={14} />
            </button>
            <button onClick={() => next && onNavigate(next.lead_id)} disabled={!next} className={iconBtn} data-testid="lead-drawer-next" title="Next lead">
              <ChevronRight size={14} />
            </button>
            {idx >= 0 && <span className={`text-[10px] ${ts} ml-1`}>{idx + 1} / {leads.length}</span>}
          </div>
          <div className="flex items-center gap-2">
            {lead && (
              <select value={lead.status} onChange={e => patch({ status: e.target.value })}
                className={`text-[11px] px-2.5 py-1.5 rounded-lg border ${line} ${dark ? 'bg-black/30 text-white' : 'bg-white text-slate-800'}`}
                data-testid="lead-drawer-status">
                {STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
              </select>
            )}
            <button onClick={onClose} className={iconBtn} data-testid="lead-drawer-close"><X size={15} /></button>
          </div>
        </div>

        {!lead ? (
          <div className="py-24 flex justify-center"><Loader2 size={22} className="animate-spin text-teal-400" /></div>
        ) : (
          <div className="flex-1 px-5 py-5 space-y-4">
            {/* Identity */}
            <div>
              <h2 className={`text-xl font-display font-black leading-tight ${tp}`} data-testid="lead-drawer-name">{lead.name}</h2>
              <div className="flex items-center gap-2 mt-2.5 flex-wrap">
                {lead.industry && (
                  <span className={`px-2.5 py-1 rounded-full border text-[10px] font-bold ${dark ? 'border-emerald-400/40 text-emerald-300 bg-emerald-400/[0.06]' : 'border-emerald-400 text-emerald-700 bg-emerald-50'}`}>
                    {lead.industry}
                  </span>
                )}
                {!lead.website && (
                  <span className={`px-2.5 py-1 rounded-full border text-[10px] font-bold ${dark ? 'border-red-400/40 text-red-300 bg-red-400/[0.07]' : 'border-red-300 text-red-600 bg-red-50'}`} data-testid="lead-drawer-no-website">
                    No Website
                  </span>
                )}
                {lead.rating != null && (
                  <span className={`flex items-center gap-1 text-[11px] ${ts}`}>
                    <Star size={11} className="text-amber-400" /> {lead.rating} ({lead.review_count} Google reviews)
                  </span>
                )}
                {lead.city && <span className={`flex items-center gap-1 text-[11px] ${ts}`}><MapPin size={10} /> {lead.city}</span>}
              </div>
            </div>

            {pkgBusy && <PackageProgress lead={lead} dark={dark} />}
            {pkgFailed && (
              <div className={`rounded-2xl border px-4 py-3 ${dark ? 'border-red-400/30 bg-red-400/[0.06]' : 'border-red-200 bg-red-50'}`} data-testid="lead-drawer-pkg-failed">
                <p className="text-sm font-bold text-red-400 flex items-center gap-2"><AlertTriangle size={14} /> Deep analysis failed — not charged</p>
                {lead.package_error && <p className={`text-xs mt-1 ${ts}`}>{lead.package_error}</p>}
              </div>
            )}

            {/* Reach out */}
            <div className={section} data-testid="lead-drawer-contact">
              <div className="space-y-2.5 text-[13px]">
                <div className="flex items-center gap-2.5">
                  <Phone size={13} className={rowIcon} />
                  {lead.phone ? (
                    <>
                      <a href={`tel:${lead.phone}`} className={`font-semibold ${tp} hover:text-teal-400`} data-testid="lead-drawer-phone">{lead.phone}</a>
                      <button onClick={() => copyText(lead.phone, 'Phone copied')} className={`${ts} hover:text-teal-400`} data-testid="lead-drawer-copy-phone"><Copy size={12} /></button>
                    </>
                  ) : <span className={ts}>No phone</span>}
                </div>
                <div className="flex items-center gap-2.5 min-w-0">
                  <Globe size={13} className={`${rowIcon} shrink-0`} />
                  {lead.website ? (
                    <a href={lead.website} target="_blank" rel="noreferrer" className="text-teal-400 hover:underline truncate flex items-center gap-1" data-testid="lead-drawer-website">
                      {lead.website.replace(/^https?:\/\/(www\.)?/, '')} <ExternalLink size={9} className="shrink-0" />
                    </a>
                  ) : <span className={`${dark ? 'text-red-300' : 'text-red-500'} font-medium`}>No website</span>}
                </div>
                <div className="flex items-center gap-2.5 min-w-0">
                  <MapPin size={13} className={`${rowIcon} shrink-0`} />
                  {lead.address ? (
                    <>
                      <span className={`${tp} truncate`}>{lead.address}</span>
                      {lead.google_maps_uri && (
                        <a href={lead.google_maps_uri} target="_blank" rel="noreferrer" className="text-teal-400 hover:underline text-[11px] shrink-0 flex items-center gap-0.5" data-testid="lead-drawer-maps">
                          Maps <ExternalLink size={8} />
                        </a>
                      )}
                    </>
                  ) : <span className={ts}>No address</span>}
                </div>
                <div className="flex items-center gap-2.5 min-w-0">
                  <Mail size={13} className={`${rowIcon} shrink-0`} />
                  {lead.email ? (
                    <>
                      <a href={`mailto:${lead.email}`} className={`${tp} hover:text-teal-400 truncate`} data-testid="lead-drawer-email">{lead.email}</a>
                      <button onClick={() => copyText(lead.email, 'Email copied')} className={`${ts} hover:text-teal-400`}><Copy size={12} /></button>
                    </>
                  ) : <span className={ts}>No email found</span>}
                </div>
              </div>

              <div className={`flex items-center justify-between gap-2 mt-3.5 pt-3.5 border-t ${line} flex-wrap`}>
                <div className="flex items-center gap-2">
                  {lead.socials?.instagram && (
                    <a href={lead.socials.instagram} target="_blank" rel="noreferrer" className={`${chipCls} border rounded-full p-2 hover:opacity-80`} data-testid="lead-drawer-instagram" title="Instagram">
                      <Instagram size={13} className="text-pink-400" />
                    </a>
                  )}
                  {lead.socials?.facebook && (
                    <a href={lead.socials.facebook} target="_blank" rel="noreferrer" className={`${chipCls} border rounded-full p-2 hover:opacity-80`} data-testid="lead-drawer-facebook" title="Facebook">
                      <Facebook size={13} className="text-blue-400" />
                    </a>
                  )}
                  {lead.socials?.linkedin && (
                    <a href={lead.socials.linkedin} target="_blank" rel="noreferrer" className={`${chipCls} border rounded-full p-2 hover:opacity-80`} data-testid="lead-drawer-linkedin" title="LinkedIn">
                      <Linkedin size={13} className="text-sky-400" />
                    </a>
                  )}
                  {!lead.socials?.instagram && !lead.socials?.facebook && !lead.socials?.linkedin && (
                    <span className={`text-[10px] ${ts}`}>{lead.website ? (lead.socials_checked ? 'No socials found on their site' : 'Checking their site for socials…') : 'No socials — no website'}</span>
                  )}
                </div>
                <button onClick={copyDetails} className={`${chipCls} border rounded-full px-3.5 py-1.5 text-[11px] font-semibold flex items-center gap-1.5 hover:opacity-80`} data-testid="lead-drawer-copy-details">
                  <ClipboardList size={11} /> Copy business details
                </button>
              </div>
            </div>

            {/* Analysis — button / progress / leak banner (under business details) */}
            {!analyzed ? ((running || a.status === 'error') && (
              <div data-testid="lead-drawer-actions">
                {running ? (
                  <div className={`w-full rounded-2xl border px-4 py-3 ${dark ? 'border-violet-400/30 bg-violet-400/[0.05]' : 'border-violet-200 bg-violet-50'}`} data-testid="lead-drawer-analyzing">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`flex items-center gap-2 text-xs font-semibold ${dark ? 'text-violet-300' : 'text-violet-700'}`}>
                        <Loader2 size={13} className="animate-spin" /> Analyzing…
                      </span>
                      <span className={`text-xs font-bold ${dark ? 'text-violet-300' : 'text-violet-700'}`} data-testid="lead-drawer-analyze-pct">{pct}%</span>
                    </div>
                    <div className={`mt-2 h-1.5 rounded-full overflow-hidden ${dark ? 'bg-white/10' : 'bg-violet-100'}`}>
                      <div className="h-full bg-violet-400 rounded-full transition-[width] duration-700" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                ) : <span className="text-[11px] text-red-400">Analysis failed: {a.error}</span>}
              </div>
            )) : (
              <div ref={bannerRef} className={`rounded-2xl border ${dark ? 'border-red-400/25 bg-red-400/[0.05]' : 'border-red-200 bg-red-50'}`} data-testid="lead-drawer-leak-banner">
                <div className="flex items-center justify-between gap-3 px-4 py-3 flex-wrap">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Flame size={18} className={a.leak_score >= 70 ? 'text-red-400' : 'text-amber-400'} />
                      <span className={`text-lg font-bold ${tp}`}>{a.leak_score}<span className={`text-xs font-normal ${ts}`}>/100</span></span>
                    </div>
                    <span className="text-lg font-bold text-red-400">${Number(a.total_monthly_leak_usd || 0).toLocaleString()}<span className={`text-xs font-normal ${ts}`}>/mo leaking</span></span>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={() => setBreakdownOpen(o => !o)}
                      className={`flex items-center gap-1 text-[11px] px-3 py-2 rounded-full font-bold border transition-colors ${dark ? 'border-red-400/40 text-red-300 hover:bg-red-400/10' : 'border-red-300 text-red-600 hover:bg-red-100'}`}
                      data-testid="lead-drawer-breakdown-btn">
                      Breakdown <ChevronDown size={11} className={`transition-transform ${breakdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {propGenerating && (
                      <span className="flex items-center gap-1.5 text-[11px] px-3.5 py-2 rounded-full bg-violet-500/60 text-white font-bold">
                        <Loader2 size={11} className="animate-spin" /> Drafting…
                      </span>
                    )}
                    {proposal && ['draft', 'sent'].includes(proposal.status) && (
                      <>
                        <button onClick={() => onOpenProposal(proposal)}
                          className="flex items-center gap-1.5 text-[11px] px-3.5 py-2 rounded-full bg-violet-500 text-white font-bold hover:bg-violet-400"
                          data-testid="lead-drawer-open-proposal-btn">
                          <FileText size={11} /> Open proposal
                        </button>
                        <button onClick={() => copyText(proposalLink, 'Proposal link copied — paste it in your reply')}
                          title="Copy proposal link"
                          className={`p-2 rounded-full border transition-colors ${dark ? 'border-violet-400/40 text-violet-300 hover:bg-violet-400/10' : 'border-violet-300 text-violet-600 hover:bg-violet-50'}`}
                          data-testid="lead-drawer-copy-proposal-link">
                          <Copy size={11} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {breakdownOpen && (
                  <div className={`px-4 pb-4 pt-3 border-t ${dark ? 'border-red-400/15' : 'border-red-200/70'}`} data-testid="lead-drawer-breakdown">
                    {a.summary && <p className={`text-[11px] leading-relaxed ${ts}`}>{a.summary}</p>}
                    <div className="space-y-2 mt-3">
                      {(a.gaps || []).map((g, i) => (
                        <div key={i} className={`rounded-xl border p-2.5 ${dark ? 'border-white/10 bg-black/20' : 'border-red-100 bg-white'}`}>
                          <div className="flex items-center justify-between gap-2">
                            <p className={`text-[11.5px] font-bold ${tp}`}>{g.title}</p>
                            <span className="text-[11px] font-bold text-red-400 whitespace-nowrap">${Number(g.est_monthly_loss_usd || 0).toLocaleString()}/mo</span>
                          </div>
                          {g.why && <p className={`text-[10.5px] mt-1 leading-relaxed ${ts}`}>{g.why}</p>}
                        </div>
                      ))}
                    </div>
                    {(a.quick_wins || []).length > 0 && (
                      <div className="mt-3">
                        <p className={`text-[9px] tracking-[0.2em] uppercase font-bold ${ts} mb-1.5`}>Quick wins with AI appointment setting</p>
                        <ul className="space-y-1">
                          {a.quick_wins.map((q, i) => (
                            <li key={i} className={`text-[10.5px] leading-relaxed ${ts} flex gap-1.5`}><span className="text-emerald-400">•</span>{q}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Deal value */}
            <div className={section} data-testid="lead-drawer-deal">
              <p className={`text-[10px] tracking-[0.24em] uppercase font-bold ${ts} mb-2.5`}>Deal Value</p>
              <div className="grid grid-cols-2 gap-3">
                {[['deal_value_upfront', 'Upfront', 'lead-drawer-deal-upfront'],
                  ['deal_value_monthly', 'Monthly', 'lead-drawer-deal-monthly']].map(([field, label, tid]) => (
                  <div key={field}>
                    <label className={`text-[10px] font-semibold ${ts} block mb-1`}>{label} ($)</label>
                    <div className="relative">
                      <span className={`absolute left-3 top-1/2 -translate-y-1/2 text-xs ${ts}`}>$</span>
                      <input type="number" min="0" step="50" key={`${field}-${leadId}`}
                        defaultValue={lead[field] || ''} placeholder="0"
                        onBlur={e => {
                          const v = Math.max(0, Number(e.target.value) || 0);
                          if (v !== (lead[field] || 0)) patch({ [field]: v });
                        }}
                        className={`w-full pl-7 pr-3 py-2 rounded-xl border ${line} ${dark ? 'bg-black/30 text-white placeholder-white/25' : 'bg-white text-slate-800 placeholder-slate-400'} text-xs font-semibold focus:outline-none focus:border-emerald-400/50`}
                        data-testid={tid} />
                    </div>
                  </div>
                ))}
              </div>
              <p className={`text-[10px] ${ts} mt-2`}>Shows on the pipeline card — set it when you quote the deal.</p>
            </div>

            {/* Outreach script */}
            <div className={section} data-testid="lead-drawer-scripts">
              <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                <p className={`text-[10px] tracking-[0.24em] uppercase font-bold ${ts}`}>Outreach Script</p>
                <div className="flex items-center gap-2">
                  <span className={`text-[9px] px-2 py-0.5 rounded-full border font-bold uppercase tracking-wide ${
                    scripts?.tier === 'detailed'
                      ? (dark ? 'border-emerald-400/40 text-emerald-300' : 'border-emerald-400 text-emerald-700')
                      : (dark ? 'border-white/15 text-white/40' : 'border-slate-300 text-slate-500')}`}
                    data-testid="lead-drawer-script-tier">
                    {scripts?.tier === 'detailed' ? 'Detailed — analysis ammo included' : 'Basic — analyze to add backup numbers'}
                  </span>
                  <button onClick={() => setTplOpen(true)}
                    className={`flex items-center gap-1 text-[10px] font-semibold ${ts} hover:text-teal-400 transition-colors`}
                    data-testid="lead-drawer-edit-templates">
                    <Pencil size={10} /> Edit templates
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-1 mb-3 flex-wrap">
                {CHANNELS.map(c => (
                  <button key={c.id} onClick={() => { setChannel(c.id); setPendingTouch(null); setDraft(null); }}
                    className={`flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-full font-semibold transition-colors ${
                      channel === c.id
                        ? (dark ? 'bg-teal-400/15 text-teal-300' : 'bg-teal-50 text-teal-700 border border-teal-200')
                        : `${ts} ${dark ? 'hover:bg-white/5' : 'hover:bg-slate-100'}`}`}
                    data-testid={`lead-drawer-script-tab-${c.id}`}>
                    <c.icon size={11} /> {c.label}
                  </button>
                ))}
              </div>
              <textarea value={draft ?? scriptText} onChange={e => setDraft(e.target.value)} spellCheck={false}
                className={`w-full whitespace-pre-wrap font-sans text-xs leading-relaxed rounded-xl border ${line} ${dark ? 'bg-black/30 text-white/80' : 'bg-white text-slate-700'} p-3.5 h-[300px] overflow-y-auto focus:outline-none focus:border-teal-400/40 resize-none`}
                data-testid="lead-drawer-script-text" />
              <div className="flex items-center justify-between gap-2 mt-3 flex-wrap">
                <button onClick={() => { copyText(draft ?? scriptText, 'Script copied'); setPendingTouch(channel); }}
                  className="flex items-center gap-1.5 text-xs px-4 py-2 rounded-full bg-teal-500 text-white font-semibold hover:bg-teal-400"
                  data-testid="lead-drawer-copy-script">
                  <Copy size={12} /> Copy script
                </button>
                {pendingTouch && (
                  <span className="flex items-center gap-2 text-[11px]" data-testid="lead-drawer-touch-prompt">
                    <span className={ts}>Mark contacted via {CH_LABEL[pendingTouch]}?</span>
                    <button onClick={() => logTouch(pendingTouch)}
                      className={`flex items-center gap-1 px-2.5 py-1 rounded-full font-bold ${dark ? 'bg-emerald-400/15 text-emerald-300' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`}
                      data-testid="lead-drawer-mark-contacted">
                      <Check size={10} /> Yes
                    </button>
                    <button onClick={() => setPendingTouch(null)} className={ts}><X size={11} /></button>
                  </span>
                )}
              </div>
              {(lead.touches || []).length > 0 && (
                <div className="flex items-center gap-1.5 mt-3 flex-wrap" data-testid="lead-drawer-touches">
                  <span className={`text-[10px] ${ts}`}>Touches:</span>
                  {[...lead.touches].reverse().slice(0, 6).map((t, i) => (
                    <span key={i} className={`${chipCls} border rounded-full px-2.5 py-1 text-[10px] font-semibold`}>
                      {CH_LABEL[t.channel] || t.channel} · {new Date(t.at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Build progress (legacy builds) */}
            {(buildJob || lead.sub_account_id) && <div className={section} data-testid="lead-drawer-client">
              <p className={`text-[10px] tracking-[0.24em] uppercase font-bold ${ts} mb-2.5`}>Build AI Agents</p>
              {lead.sub_account_id && !subCreating && (
                <p className={`flex items-center gap-1.5 text-[11px] mb-2.5 ${ts}`} data-testid="lead-drawer-linked-sub">
                  <Building2 size={12} className="text-teal-400" /> Linked sub-account:{' '}
                  <span className="text-teal-400 font-semibold">{subs.find(s => s.sub_account_id === lead.sub_account_id)?.name || lead.sub_account_id}</span>
                </p>
              )}

              {/* Live progress bar (mirrors the deep-analysis card) */}
              {buildRunning && (() => {
                const stepStates = BUILD_STEPS.map(([k]) => buildJob?.progress?.steps?.[k]?.status || 'pending');
                const doneCount = stepStates.filter(s => ['done', 'failed', 'skipped'].includes(s)).length;
                const runningIdx = stepStates.findIndex(s => s === 'running');
                const buildPct = Math.min(99, doneCount * 25 + (runningIdx >= 0 ? 12 : 0));
                const stepNum = Math.min(4, doneCount + 1);
                const label = runningIdx >= 0 ? BUILD_STEPS[runningIdx][1] : (BUILD_STEPS[doneCount]?.[1] || 'Finishing…');
                return (
                  <div className={`rounded-2xl border px-4 py-3 mb-3 ${dark ? 'border-teal-400/30 bg-teal-400/[0.05]' : 'border-teal-200 bg-teal-50'}`} data-testid="lead-drawer-build-banner">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className={`flex items-center gap-2 text-xs font-semibold ${dark ? 'text-teal-300' : 'text-teal-700'}`}>
                        <Loader2 size={13} className="animate-spin shrink-0" /> Building AI Agents — step {stepNum} of 4: {label}
                      </span>
                      <span className={`text-xs font-bold ${dark ? 'text-teal-300' : 'text-teal-700'}`} data-testid="lead-drawer-build-pct">{buildPct}%</span>
                    </div>
                    <div className={`mt-2 h-1.5 rounded-full overflow-hidden ${dark ? 'bg-white/10' : 'bg-teal-100'}`}>
                      <div className="h-full bg-teal-400 rounded-full transition-[width] duration-700" style={{ width: `${buildPct}%` }} />
                    </div>
                  </div>
                );
              })()}

              {(buildRunning || buildJob?.status === 'done' || buildJob?.status === 'error') && (
                <div className={`rounded-xl border p-3 mb-3 ${dark ? 'border-white/10 bg-black/20' : 'border-slate-200 bg-white'}`} data-testid="lead-drawer-build-steps">
                  <div className="space-y-2">
                    {BUILD_STEPS.map(([k, label]) => {
                      const st = buildJob?.progress?.steps?.[k] || {};
                      const s = st.status || (buildJob?.status === 'error' ? 'pending' : 'pending');
                      return (
                        <div key={k} className="flex items-start gap-2" data-testid={`build-step-${k}`}>
                          {s === 'done' ? <Check size={13} className="text-emerald-400 mt-0.5 shrink-0" />
                            : s === 'running' ? <Loader2 size={13} className="animate-spin text-teal-400 mt-0.5 shrink-0" />
                              : s === 'failed' ? <AlertTriangle size={13} className="text-amber-400 mt-0.5 shrink-0" />
                                : <CircleDot size={13} className={`${ts} mt-0.5 shrink-0 opacity-40`} />}
                          <div className="min-w-0">
                            <p className={`text-[11.5px] font-semibold leading-tight ${s === 'pending' ? ts : tp}`}>{label}</p>
                            {st.detail && <p className={`text-[10px] mt-0.5 leading-snug ${ts}`}>{st.detail}</p>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {buildJob?.status === 'error' && (
                    <p className="text-[11px] text-red-400 mt-2.5" data-testid="lead-drawer-build-error">Build failed: {buildJob.error}</p>
                  )}
                  {buildJob?.status === 'done' && (
                    <div className="flex items-center gap-2 mt-3 flex-wrap">
                      <span className={`text-[11px] font-bold ${dark ? 'text-emerald-300' : 'text-emerald-700'}`} data-testid="lead-drawer-build-done">
                        ✓ {buildJob?.progress?.agent_name || 'AI agent'} is live
                      </span>
                      {proposal && ['draft', 'sent'].includes(proposal.status) && (
                        <button onClick={() => onOpenProposal(proposal)}
                          className="flex items-center gap-1.5 text-[11px] px-3.5 py-2 rounded-full bg-violet-500 text-white font-bold hover:bg-violet-400"
                          data-testid="lead-drawer-build-open-proposal">
                          <FileText size={11} /> Open proposal
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

            </div>}

          </div>
        )}
        {lead && (
          <div className={`sticky bottom-0 z-10 px-5 py-4 border-t ${line} ${panelBg}`} data-testid="lead-drawer-footer">
            {proposal && ['draft', 'sent'].includes(proposal.status) ? (
              <button onClick={() => onOpenProposal(proposal)} data-testid="lead-drawer-open-proposal-cta"
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-violet-500 text-white text-sm font-bold hover:bg-violet-400 transition-colors">
                <FileText size={15} /> Open Proposal
              </button>
            ) : (
              <button onClick={analyze} disabled={!lead.website || running || buildRunning || pkgBusy || lead.package_status === 'ready'} data-testid="lead-drawer-analyze-btn"
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-violet-500 text-white text-sm font-bold hover:bg-violet-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                {running || buildRunning || pkgBusy ? <Loader2 size={15} className="animate-spin" /> : pkgFailed ? <RotateCcw size={15} /> : <Sparkles size={15} />}
                {!lead.website ? 'No website — deep analysis needs one' : lead.package_status === 'queued' ? 'Queued…' : running || buildRunning || pkgBusy ? 'Analyzing…' : lead.package_status === 'ready' ? 'Proposal ready' : pkgFailed ? 'Retry Deep Analysis' : 'Run Deep Analysis & Proposal'}
              </button>
            )}
          </div>
        )}
      </div>
      {tplOpen && (
        <ScriptTemplatesModal theme={theme} api={api} workspaceId={workspaceId}
          onClose={() => setTplOpen(false)}
          onSaved={() => { setTplOpen(false); setDraft(null); load(); }} />
      )}
    </div>
  );
};

export default LeadDrawer;
